//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import { theme } from '@bvengineering/unison-core';
import React, { useState } from 'react';
import styled from 'styled-components';

import {
  ContentLibraryContentGroup,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import { IUserInfo } from '@apis/users/usersApiModel';
import Button from '@components/atoms/button/Button';
import { IButtonStateStyles } from '@components/atoms/button/buttonStyles';
import {
  CenteredSpinner,
  InlineSpinner,
} from '@components/atoms/spinner/Spinner';
import { SectionText } from '@components/atoms/text/Text';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { MediaSourcesDropdownSection } from '@content-drawer/filters/MediaSourcesDropdownSection';
import {
  ALL_MEDIA_SOURCE,
  MediaSource,
  mediaSection,
  useMediaSourcesSections,
} from '@content-drawer/filters/useMediaSourcesSections';
import { TrackingIdPrefix } from '@models/TrackingId';
import { isDefined } from '@utils/filterPredicates';

export const DEFAULT_USER_IDS_FILTER_SECTION: FilterSection<string> = {
  options: [],
  paramKey: 'userIds',
};

export const ALL_MEDIA_SOURCES: IContentLibrarySourcesOptions = {
  contentGroup: ContentLibraryContentGroup.ALL,
};

const triggerButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'default',
    borderThickness: 'thin',
  },
  standard: {
    color: theme.colors.aliases.bodyPrimary,
    backgroundColor: theme.colors.white,
    borderColor: 'inherit',
  },
  hover: {
    color: theme.colors.aliases.bodyPrimary,
    backgroundColor: theme.colors.white,
    borderColor: 'inherit',
  },
  active: {
    color: theme.colors.aliases.bodyPrimary,
    backgroundColor: theme.colors.gray00,
    borderColor: 'inherit',
  },
  disabled: {
    color: theme.colors.gray4,
    backgroundColor: theme.colors.gray0,
    borderColor: 'inherit',
  },
};

export interface MediaSourcesDropdownProps {
  className?: string;
  container?: HTMLElement;
  onChangeSources: (value: IContentLibrarySourcesOptions) => void;
  onChangeUserIds: (section: FilterSection<FilterValue>) => void;
  searchTrackingIdPrefix: TrackingIdPrefix;
  sources?: IContentLibrarySourcesOptions;
  userIds?: FilterSection<string>;
}

export const MediaSourcesDropdown = ({
  className,
  container,
  onChangeSources,
  onChangeUserIds,
  searchTrackingIdPrefix,
  sources = {},
  userIds = DEFAULT_USER_IDS_FILTER_SECTION,
}: MediaSourcesDropdownProps) => {
  const [filteredUsers, setFilteredUsers] = useState<IUserInfo[]>();
  const { allUsers, isLoading, sections } =
    useMediaSourcesSections(filteredUsers);

  const selectMediaSource = (source: MediaSource) => {
    const userIds = source.contentGroup ? [] : [source.id];

    if (userIds.length === 0) {
      onChangeSources({ contentGroup: source.contentGroup });
      onChangeUserIds({
        options: [],
        paramKey: 'userIds',
      });
    } else {
      onChangeSources({});
      // In the context of the content drawer, we can select just one user
      onChangeUserIds({
        options: [
          {
            isSelected: true,
            label: source.label,
            value: source.id,
          },
        ],
        paramKey: 'userIds',
      });
    }
  };

  /**
   * The media source can be a specific user, a team media, or all media.
   * The function getSelectedMediaSource will generate a media source object.
   * If the provided source matches a team media or all media content group, the appropriate media source will be selected.
   * if a user ID is provided, that user will be selected.
   * All media will be the default option.
   */
  const getSelectedMediaSource = (): MediaSource => {
    if (sources.contentGroup) {
      const mediaSource = mediaSection.sources.find(
        (source) => source.contentGroup === sources.contentGroup,
      );
      if (isDefined(mediaSource)) {
        return mediaSource;
      }
    }
    const userOption = userIds.options.find((option) => option.isSelected);
    if (isDefined(userOption)) {
      return {
        id: userOption.value,
        label: userOption.label,
      };
    }
    return ALL_MEDIA_SOURCE;
  };

  const selectedMediaSource = getSelectedMediaSource();

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild className={className}>
        <Button
          buttonStyles={triggerButtonStyles}
          isDisabled={isLoading}
          isDropdown={true}
        >
          <SectionText>
            {selectedMediaSource.label}
            {isLoading && <Spinner />}
          </SectionText>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content container={container}>
        <DropdownMenu.Search
          handleClear={() => setFilteredUsers(allUsers)}
          handleSearch={(_, filteredItems) => setFilteredUsers(filteredItems)}
          itemsToSearch={allUsers}
          searchBarConfig={{
            placeholder: 'Search users ...',
            trackingIdPrefix: searchTrackingIdPrefix,
            visuallyHiddenLabel: 'Search users',
          }}
          searchKeys={['firstName', 'lastName']}
        />
        {isLoading && <CenteredSpinner />}
        {sections.map((section) => (
          <MediaSourcesDropdownSection
            key={section.label}
            onSelect={selectMediaSource}
            section={section}
            selected={selectedMediaSource}
          />
        ))}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

const Spinner = styled(InlineSpinner)(
  ({ theme }) => `
    margin-left: ${theme.spacings[2]};
  `,
);
